// AzuraOS Progressive Web App Update Service
// Handles proactive update checking, background sync, and seamless client reloads for standalone home-screen PWAs

export interface PWAUpdateState {
  hasUpdate: boolean;
  isChecking: boolean;
  lastChecked: Date | null;
  registration: ServiceWorkerRegistration | null;
}

type UpdateListener = (hasUpdate: boolean) => void;

let updateListeners: UpdateListener[] = [];
let swRegistration: ServiceWorkerRegistration | null = null;
let updateAvailable = false;
let isRefreshing = false;
let isUserTriggeredUpdate = false;

export function registerUpdateListener(listener: UpdateListener): () => void {
  updateListeners.push(listener);
  // Immediate callback with current status
  listener(updateAvailable);
  return () => {
    updateListeners = updateListeners.filter((l) => l !== listener);
  };
}

function notifyListeners(hasUpdate: boolean) {
  updateAvailable = hasUpdate;
  updateListeners.forEach((fn) => {
    try {
      fn(hasUpdate);
    } catch (err) {
      console.warn('PWA update listener error:', err);
    }
  });
}

/**
 * Initializes the service worker with active update polling and lifecycle listeners
 */
export function initPWAUpdateService() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  // Google Web Rendering Service (WRS) / search bots explicitly reject service workers by design.
  const isBotOrCrawler = /bot|googlebot|crawler|spider|inspection|headless|lighthouse/i.test(navigator.userAgent || '') || !!(navigator as unknown as { webdriver?: boolean }).webdriver;
  if (isBotOrCrawler) {
    return;
  }

  // In AI Studio development, preview iframes, or local dev server, do NOT register service worker.
  // Proactively unregister any previously registered service workers to prevent intercepting Vite dev modules and degrading load times.
  const isDevOrPreview = 
    Boolean((import.meta as any)?.env?.DEV) || 
    window.location.hostname === 'localhost' || 
    window.location.hostname.includes('ais-dev') || 
    window.location.hostname.includes('ais-pre') || 
    window.location.hostname.includes('run.app');

  if (isDevOrPreview) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const reg of registrations) {
        reg.unregister().catch(() => {});
      }
    }).catch(() => {});
    if ('caches' in window) {
      caches.keys().then((keys) => {
        for (const k of keys) {
          if (k.startsWith('azuraos-cache-')) {
            caches.delete(k).catch(() => {});
          }
        }
      }).catch(() => {});
    }
    return;
  }

  // Reload page ONLY when the user explicitly triggers an update from the UI
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (isUserTriggeredUpdate && !isRefreshing) {
      isRefreshing = true;
      console.log('PWA: User-confirmed update taking control, reloading page...');
      window.location.reload();
    }
  });

  // Listen for broadcast messages from sw.js
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data && (event.data.type === 'AZURAOS_SW_UPDATED' || event.data.type === 'AZURAOS_UPDATE_AVAILABLE')) {
      console.log('PWA: Update notification received from service worker');
      notifyListeners(true);
    }
  });

  const registerSW = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' });
      swRegistration = registration;

      // 1. If an updated worker is already waiting in background
      if (registration.waiting) {
        console.log('PWA: An updated service worker is installed and waiting for user approval');
        notifyListeners(true);
      }

      // 2. Track new updates during this session
      registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;
        if (!installingWorker) return;

        installingWorker.addEventListener('statechange', () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // Existing page already controlled: this is a real update
              console.log('PWA: New version downloaded in background; waiting for user action');
              notifyListeners(true);
            } else {
              // First-time install for offline caching
              console.log('PWA: Content cached for offline use');
            }
          }
        });
      });

      // 3. Proactive update check: when user returns to app after being away
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          checkForPWAUpdate();
        }
      });

      window.addEventListener('online', () => {
        checkForPWAUpdate();
      });

      // 4. Periodic polling every 30 minutes in background
      setInterval(() => {
        checkForPWAUpdate();
      }, 30 * 60 * 1000);

    } catch (err: unknown) {
      // Crawlers (like Google WRS), sandboxed iframes, or private browsing modes may reject SW registration.
      // Keep console completely clean unless in local development with an unexpected error.
      const isExpectedSandboxRejection = err instanceof Error && (err.message.includes('Rejected') || err.name === 'SecurityError');
      if (!isExpectedSandboxRejection && process.env.NODE_ENV === 'development') {
        console.warn('PWA service worker registration notice:', err);
      }
    }
  };

  if (document.readyState === 'complete') {
    registerSW();
  } else {
    window.addEventListener('load', registerSW);
  }
}

/**
 * Manually or programmatically triggers an update check
 */
export async function checkForPWAUpdate(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;

  try {
    const reg = swRegistration || (await navigator.serviceWorker.getRegistration());
    if (!reg) return false;

    swRegistration = reg;
    await reg.update();

    if (reg.waiting) {
      notifyListeners(true);
      return true;
    }

    return updateAvailable;
  } catch (err) {
    console.warn('PWA update check notice:', err);
    return false;
  }
}

/**
 * Applies the waiting update immediately
 */
export function applyPWAUpdate() {
  isUserTriggeredUpdate = true;

  if (!('serviceWorker' in navigator)) {
    window.location.reload();
    return;
  }

  try {
    if (swRegistration?.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg?.waiting) {
          reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        } else {
          // Hard reload with timestamp cache-buster
          window.location.href = window.location.href.split('?')[0] + '?v=' + Date.now();
        }
      });
    }

    // Safety fallback reload if controllerchange doesn't fire within 800ms
    setTimeout(() => {
      if (!isRefreshing) {
        isRefreshing = true;
        window.location.reload();
      }
    }, 800);
  } catch {
    window.location.reload();
  }
}
